import React, { useState, Suspense } from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaWhatsapp, FaClock, FaQuestion, FaChevronDown } from 'react-icons/fa';
import emailjs from 'emailjs-com';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SEO from '../components/SEO/SEO';


const ThreeBackground = React.lazy(() => import('../components/ThreeBackground'));


const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeFAQ, setActiveFAQ] = useState(null); // For collapsible FAQ

  const contactInfo = [
    {
      icon: <FaMapMarkerAlt className="text-2xl" aria-label="Location" />,
      title: "Location",
      detail: "Bogura, Bangladesh",
    },
    {
      icon: <FaClock className="text-2xl" aria-label="Office Hours" />,
      title: "Office Hours",
      detail: "24/7",
    },
    {
      icon: <FaWhatsapp className="text-2xl" aria-label="Phone" />,
      title: "Phone",
      detail: "+880 1998470273",
    }
  ];

  const faqs = [
    {
      question: "What's your typical response time?",
      answer: "Usually within 12 hours."
    },
    {
      question: "Do you work remotely?",
      answer: "Yes, I work with clients worldwide."
    },
    {
      question: "What services do you offer?",
      answer: "I specialize in web development, including frontend, backend, and full-stack solutions."
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await emailjs.send(
        'service_svxtn0d',
        'template_vx70ysf',
        formData,
        '6pRF5XaBmj1du2b9k'
      );
      toast.success('Email sent successfully!');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send Email. Please try again.');
    }

    setIsSubmitting(false);
  };

  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white relative overflow-hidden">
      <SEO 
        title="Mahmudur Rahman - Contact Now"
        description="Full Stack Developer specializing in MERN stack development. Building modern web applications with React, Node.js, and MongoDB. Contact Form"
        image="https://res.cloudinary.com/dqovjmmlx/image/upload/v1740490681/conatct-banner_wktvav.jpg"
        url="https://devplus.fun/conatct"
        keywords="Full Stack Developer, MERN Stack, React, Node.js, MongoDB, Web Development, JavaScript, Next.js, Express.js, contact, mahmudur rahman contact, contact form, contact now, contact me, contact mahmud, hire developer, freelancer developer, web developer contact, connect with developer, software engineer contact, email contact, mahmud's contact, farhan's email, mahmudur rahman's contact"
      />

      {/* 3D Background */}
      <Suspense fallback={<div className="absolute inset-0 z-0 bg-gray-900" />}>
        <div className="absolute inset-0 z-0">
          <ThreeBackground />
        </div>
      </Suspense>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-4">
            Get In Touch
          </h1>
          <p className="text-gray-300 text-lg">
            Let's discuss your project and make something amazing together
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -5 }}
                  className="bg-white/5 backdrop-blur-lg p-6 rounded-xl"
                >
                  <div className="text-blue-400 mb-4">{info.icon}</div>
                  <h3 className="text-lg font-semibold mb-2">{info.title}</h3>
                  <p className="text-gray-400">{info.detail}</p>
                </motion.div>
              ))}
            </div>

            {/* Collapsible FAQ Section */}
            <div className="bg-white/5 backdrop-blur-lg p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <FaQuestion className="text-blue-400" />
                Frequently Asked Questions
              </h3>
              <div className="space-y-2">
                {faqs.map((faq, index) => (
                  <div key={index} className="p-4 bg-white/5 rounded-lg">
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="w-full flex justify-between items-center text-left"
                    >
                      <h4 className="font-medium">{faq.question}</h4>
                      <FaChevronDown
                        className={`transform transition-transform ${activeFAQ === index ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {activeFAQ === index && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        transition={{ duration: 0.3 }}
                        className="text-gray-400 text-sm mt-2"
                      >
                        {faq.answer}
                      </motion.p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white/10 backdrop-blur-lg p-6 sm:p-8 rounded-xl shadow-xl"
          >
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <motion.div whileTap={{ scale: 0.99 }}>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 sm:py-4 bg-white/5 border border-gray-300/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm sm:text-base"
                  placeholder="Your Name"
                  aria-label="Your Name"
                />
              </motion.div>

              <motion.div whileTap={{ scale: 0.99 }}>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 sm:py-4 bg-white/5 border border-gray-300/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm sm:text-base"
                  placeholder="Your Email"
                  aria-label="Your Email"
                />
              </motion.div>

              <motion.div whileTap={{ scale: 0.99 }}>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  className="w-full px-4 py-3 sm:py-4 bg-white/5 border border-gray-300/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm sm:text-base"
                  placeholder="Subject"
                  aria-label="Subject"
                />
              </motion.div>

              <motion.div whileTap={{ scale: 0.99 }}>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows="5"
                  className="w-full px-4 py-3 sm:py-4 bg-white/5 border border-gray-300/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none text-sm sm:text-base"
                  placeholder="Your Message"
                  aria-label="Your Message"
                />
              </motion.div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 sm:py-4 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium text-sm sm:text-base
                  ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-lg'}`}
                aria-label="Send Message"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </span>
                ) : 'Send Message'}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>

      <ToastContainer 
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
};

export default Contact;